import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./WelcomeVideoSliceModal.css";

export const WELCOME_VIDEO_MAX_SLICE_SEC = 30;
const MIN_SLICE_SEC = 1;

export function formatVideoClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function formatSliceLength(seconds) {
  const value = Math.max(0, Number(seconds) || 0);
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function readVideoDuration(url) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = video.duration;
      video.removeAttribute("src");
      video.load();
      resolve(duration);
    };
    video.onerror = () => reject(new Error("unreadable"));
    video.src = url;
  });
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export default function WelcomeVideoSliceModal({
  url,
  duration,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const rangeRef = useRef({ start: 0, end: 0 });
  const safeDuration = Math.max(0, Number(duration) || 0);
  const maxSpan = Math.min(WELCOME_VIDEO_MAX_SLICE_SEC, safeDuration);
  const minSpan = Math.min(MIN_SLICE_SEC, safeDuration);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(maxSpan);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const keepInside = () => {
      if (dragRef.current) return;
      if (video.currentTime >= end - 0.05) {
        video.currentTime = start;
      }
    };

    const playFromStart = () => {
      if (dragRef.current) return;
      video.currentTime = start;
      video.play().catch(() => {});
    };

    video.addEventListener("timeupdate", keepInside);
    if (video.readyState >= 1) playFromStart();
    else video.addEventListener("loadedmetadata", playFromStart, { once: true });

    return () => video.removeEventListener("timeupdate", keepInside);
  }, [start, end, url]);

  useEffect(() => {
    const move = (event) => {
      const drag = dragRef.current;
      const track = trackRef.current;
      if (!drag || !track || drag.pointerId !== event.pointerId) return;
      const rect = track.getBoundingClientRect();
      const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
      const point = clamp(ratio * safeDuration, 0, safeDuration);

      const video = videoRef.current;
      let nextStart = start;
      let nextEnd = end;

      if (drag.mode === "start") {
        nextStart = clamp(point, Math.max(0, end - maxSpan), end - minSpan);
        setStart(nextStart);
      } else if (drag.mode === "end") {
        nextEnd = clamp(point, start + minSpan, Math.min(safeDuration, start + maxSpan));
        setEnd(nextEnd);
      } else {
        nextStart = clamp(point - drag.offset, 0, Math.max(0, safeDuration - drag.length));
        nextEnd = nextStart + drag.length;
        setStart(nextStart);
        setEnd(nextEnd);
      }

      rangeRef.current = { start: nextStart, end: nextEnd };
      if (video) {
        const frame = drag.mode === "end" ? Math.max(nextStart, nextEnd - 0.08) : nextStart;
        if (Math.abs(video.currentTime - frame) > 0.03) video.currentTime = frame;
      }
    };

    const stop = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = rangeRef.current.start;
      video.play().catch(() => {});
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
  }, [end, maxSpan, minSpan, safeDuration, start]);

  if (!url) return null;

  const beginDrag = (mode) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
    const point = clamp(ratio * safeDuration, 0, safeDuration);
    rangeRef.current = { start, end };
    dragRef.current = {
      mode,
      pointerId: event.pointerId,
      offset: point - start,
      length: end - start,
    };
    const video = videoRef.current;
    if (video) video.pause();
  };

  const startPct = safeDuration > 0 ? (start / safeDuration) * 100 : 0;
  const endPct = safeDuration > 0 ? (end / safeDuration) * 100 : 100;

  return (
    <div className="video-slice" role="dialog" aria-modal="true">
      <div className="video-slice__panel">
        <h3 className="video-slice__title">{t("videoSliceTitle")}</h3>
        <p className="video-slice__hint">{t("videoSliceHint")}</p>
        <video
          ref={videoRef}
          className="video-slice__player"
          src={url}
          muted
          playsInline
          loop
          preload="metadata"
        />
        <div className="video-slice__meta">
          <span>{formatVideoClock(start)}</span>
          <strong>
            {t("videoSliceLength", {
              seconds: formatSliceLength(end - start),
              max: WELCOME_VIDEO_MAX_SLICE_SEC,
            })}
          </strong>
          <span>{formatVideoClock(end)}</span>
        </div>
        <div
          ref={trackRef}
          className="video-slice__track"
          onPointerDown={beginDrag("window")}
        >
          <div
            className="video-slice__selection"
            style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
          />
          <button
            type="button"
            className="video-slice__handle video-slice__handle--start"
            style={{ left: `${startPct}%` }}
            aria-label={t("videoSliceStart")}
            onPointerDown={beginDrag("start")}
          />
          <button
            type="button"
            className="video-slice__handle video-slice__handle--end"
            style={{ left: `${endPct}%` }}
            aria-label={t("videoSliceEnd")}
            onPointerDown={beginDrag("end")}
          />
        </div>
        <div className="video-slice__actions">
          <button type="button" className="video-slice__cancel" onClick={onCancel}>
            {t("cancel")}
          </button>
          <button
            type="button"
            className="video-slice__confirm"
            onClick={() => onConfirm(start, end)}
          >
            {t("videoSliceUse")}
          </button>
        </div>
      </div>
    </div>
  );
}
